import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Group,
  Text,
  Alert,
  Stack,
  Progress,
  Card,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import {
  IconUpload,
  IconX,
  IconFile,
  IconFileSpreadsheet,
  IconAlertCircle,
  IconDownload,
} from '@tabler/icons-react';
import { parseFile } from '../../../utils/file-parser';
import { ParsedFileData } from '../../../types/geneva-schema';
import { generateExcelTemplate } from '../../../utils/excel-template-generator';
import { FieldWithMetadata, MappingField } from '../../../utils/geneva-field-helpers';

interface BulkFileUploadProps {
  onFileProcessed: (data: ParsedFileData) => void;
  onError: (error: string) => void;
  disabled?: boolean;
  // Template generation props
  recordTypeName?: string;
  availableFields?: MappingField[];
  availableFieldMetadata?: FieldWithMetadata[];
}

export const BulkFileUpload: React.FC<BulkFileUploadProps> = ({
  onFileProcessed,
  onError,
  disabled = false,
  recordTypeName,
  availableFields,
  availableFieldMetadata,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [processingProgress, setProcessingProgress] = useState(0);

  const templateFields = useMemo(() => {
    const MAX_ARRAY_INSTANCES = 5;

    if (!availableFields || availableFields.length === 0) {
      return [];
    }

    const metaMap = new Map<string, FieldWithMetadata>();
    availableFieldMetadata?.forEach((meta) => {
      const key = meta.uniqueFieldId || meta.xmlTag;
      metaMap.set(key, meta);
    });

    const result: MappingField[] = [];
    const seen = new Set<string>();

    const addField = (field: MappingField) => {
      if (seen.has(field.xmlTag)) {
        return;
      }
      seen.add(field.xmlTag);
      result.push(field);
    };

    const findMetadata = (field: MappingField): FieldWithMetadata | undefined => {
      if (metaMap.size === 0) {
        return undefined;
      }

      const candidates = [
        field.uniqueFieldId,
        field.xmlTag,
        field.originalXmlTag,
      ].filter(Boolean) as string[];

      for (const candidate of candidates) {
        const meta = metaMap.get(candidate);
        if (meta) {
          return meta;
        }
      }

      return undefined;
    };

    const withBracketedArray = (fieldTag: string, arrayPath: string, instance: number): string => {
      if (!arrayPath) {
        return fieldTag;
      }
      return fieldTag.replace(arrayPath, `${arrayPath}[${instance}]`);
    };

    availableFields.forEach((field) => {
      const metadata = findMetadata(field);
      if (metadata?.isHidden) {
        return;
      }

      addField(field);

      const arrayContext = metadata?.arrayContext;
      if (!arrayContext) {
        return;
      }

      for (let instance = 2; instance <= MAX_ARRAY_INSTANCES; instance += 1) {
        const bracketedTag = withBracketedArray(field.xmlTag, arrayContext, instance);
        addField({
          ...field,
          xmlTag: bracketedTag,
          required: false,
          description: field.description
            ? `${field.description} (Instance ${instance})`
            : `${field.originalXmlTag} (Instance ${instance})`,
        });
      }
    });

    return result;
  }, [availableFields, availableFieldMetadata]);

  const handleDownloadTemplate = async () => {
    if (!recordTypeName || templateFields.length === 0) {
      onError('No fields available for template generation');
      return;
    }

    try {
      await generateExcelTemplate({
        recordTypeName,
        fields: templateFields,
        includeExamples: true,
      });
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to generate template');
    }
  };

  const handleFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    setUploadedFile(file);
    setIsProcessing(true);
    setProcessingProgress(10);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev < 80) return prev + 10;
          return prev;
        });
      }, 200);

      const result = await parseFile(file);

      clearInterval(progressInterval);
      setProcessingProgress(100);

      if (result.success && result.data) {
        onFileProcessed(result.data);
        setTimeout(() => {
          setProcessingProgress(0);
          setIsProcessing(false);
        }, 500);
      } else {
        setIsProcessing(false);
        setProcessingProgress(0);
        setUploadedFile(null);
        onError(result.error || 'Failed to process file');
      }
    } catch (error) {
      setIsProcessing(false);
      setProcessingProgress(0);
      setUploadedFile(null);
      onError(error instanceof Error ? error.message : 'Unknown error occurred');
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setIsProcessing(false);
    setProcessingProgress(0);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (uploadedFile && !isProcessing) {
    return (
      <Card withBorder>
        <Group justify="space-between">
          <Group gap="sm">
            <IconFileSpreadsheet size={24} color="var(--mantine-color-blue-6)" />
            <Box>
              <Text fw={500}>{uploadedFile.name}</Text>
              <Text size="sm" c="dimmed">
                {formatFileSize(uploadedFile.size)}
              </Text>
            </Box>
          </Group>

          <Button
            variant="light"
            color="red"
            size="sm"
            leftSection={<IconX size={16} />}
            onClick={handleRemoveFile}
            disabled={disabled}
          >
            Remove
          </Button>
        </Group>
      </Card>
    );
  }

  return (
    <Stack gap="sm">
      {/* Download Template Button */}
      {recordTypeName && templateFields.length > 0 && (
        <Group justify="center" mb="sm">
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadTemplate}
            disabled={disabled}
          >
            Download {recordTypeName} Template
          </Button>
        </Group>
      )}

      <Dropzone
        onDrop={handleFileUpload}
        onReject={(files) => onError(`Rejected files: ${files.map(f => f.file.name).join(', ')}`)}
        maxSize={10 * 1024 * 1024} // 10MB
        accept={[
          MIME_TYPES.csv,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
          'application/vnd.ms-excel', // .xls
        ]}
        disabled={disabled || isProcessing}
        multiple={false}
      >
        <Group justify="center" gap="xl" mih={220} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload
              size="3.2rem"
              stroke={1.5}
              color="var(--mantine-color-blue-6)"
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX
              size="3.2rem"
              stroke={1.5}
              color="var(--mantine-color-red-6)"
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconFile size="3.2rem" stroke={1.5} />
          </Dropzone.Idle>

          <Box>
            <Text size="xl" inline>
              Drag Excel or CSV file here or click to select
            </Text>
            <Text size="sm" c="dimmed" inline mt={7}>
              Upload a file containing your bulk data. Maximum file size is 10MB.
              Supported formats: .csv, .xlsx, .xls
            </Text>
          </Box>
        </Group>
      </Dropzone>

      {isProcessing && (
        <Box>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>Processing file...</Text>
            <Text size="sm" c="dimmed">{processingProgress}%</Text>
          </Group>
          <Progress value={processingProgress} animated />
        </Box>
      )}

      <Alert
        icon={<IconAlertCircle size={16} />}
        title="File Requirements"
        color="blue"
        variant="light"
      >
        <Text size="sm">
          • First row should contain column headers<br />
          • Each subsequent row represents one record<br />
          • Empty rows will be skipped<br />
          • Supported formats: CSV (.csv), Excel (.xlsx, .xls)<br />
          {recordTypeName && availableFields && availableFields.length > 0 && (
            <>• <strong>Tip:</strong> Use the "Download Template" button above to get a pre-formatted Excel file</>
          )}
        </Text>
      </Alert>
    </Stack>
  );
};